import "../Offers/OffersListing/CreateOffer.css";
import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { failed, success } from "@components/Common/Toastify";
import { useUpdateOfferMutation, useViewOffersQuery } from "../../API/Offers";
import storage from "@utils/storage";
import { defaultParams } from "@utils/global.constants";

export const UpdateOffer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const offerId = location.state?.offerId;
    const [btnLoading, setBtnLoading] = useState(false);
    const [params] = useState(defaultParams);
    const { mutateAsync: updateOfferMutate } = useUpdateOfferMutation();
    const Id = storage.getUser();
    const userId = Id?.user?.userId;

    // Only fetch offer data if offerId exists
    const { data } =
        useViewOffersQuery({
            offerId: offerId,
            params: { ...params },
        });

    const hasApplications = data?.applications?.length > 0;

    const formik = useFormik({
        initialValues: {
            lenderId: userId,
            title: "",
            loanAmount: "",
            interestRate: "",
            loanDuration: "",
            termsAndConditions: "",
        },
        validationSchema: yup.object({
            lenderId: yup.string().required("Lender ID is required"),
            title: yup.string().required("Title is required"),
            loanAmount: yup
                .number()
                .required("Loan amount is required")
                .min(10, "Minimum loan amount is 10"),
            interestRate: yup
                .number()
                .required("Interest rate is required")
                .min(0.1, "Interest rate must be greater than 0"),
            loanDuration: yup
                .number()
                .required("Loan duration is required")
                .min(1, "Minimum duration is 1 month"),
            termsAndConditions: yup
                .string()
                .required("Terms and conditions are required"),
        }),
        onSubmit: async (values) => {
            setBtnLoading(true);
            try {
                const response = await updateOfferMutate({ offerId, payload: values });
                success(response?.data?.message || "Offer updated!");
                navigate("/app/all-offers");
            } catch (err: any) {
                failed(err?.response?.data?.message || "Offer update failed.");
            } finally {
                setBtnLoading(false);
            }
        },
    });

    // Populate form on load
    useEffect(() => {
        if (offerId && data) {
            const offer = data;
            const currentValues = formik.values;
            const newValues = {
                lenderId: offer.lenderId || userId,
                title: offer.title || "",
                loanAmount: offer.loanAmount || "",
                interestRate: offer.interestRate || "",
                loanDuration: offer.loanDuration || "",
                termsAndConditions: offer.termsAndConditions || "",
            };

            const valuesAreSame = JSON.stringify(currentValues) === JSON.stringify(newValues);
            if (!valuesAreSame) {
                formik.setValues(newValues);
            }
        }
    }, [offerId, data, userId]);


    return (
        <div className="content create-offer-section">
            <div className="content-wrapper">
                <div className="input-btn-fields d-flex align-items-center">
                    <button className="back-btn mb-4" onClick={() => navigate(-1)}>
                        Back
                    </button>
                </div>
                <div className="outer-box-wrapper">
                    <form className="lender-form" onSubmit={formik.handleSubmit}>
                        <div className="row mb-4">
                            <div className="col-lg-6 email-name-label">
                                <label htmlFor="title" className="form-label">
                                    Title <span className="asterisk">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    placeholder="Title"
                                    {...formik.getFieldProps("title")}
                                />
                                {formik.touched.title && formik.errors.title && (
                                    <div className="text-danger">{formik.errors.title}</div>
                                )}
                            </div>
                            <div className="col-lg-6">
                                <label htmlFor="loanAmount" className="form-label">
                                    Loan Amount <span className="asterisk">*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="loanAmount"
                                    placeholder="Loan amount"
                                    disabled={hasApplications}
                                    {...formik.getFieldProps("loanAmount")}
                                />
                                {formik.touched.loanAmount && formik.errors.loanAmount && (
                                    <div className="text-danger">{formik.errors.loanAmount}</div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-lg-6 email-name-label">
                                <label htmlFor="loanDuration" className="form-label">
                                    Loan Duration (months) <span className="asterisk">*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="loanDuration"
                                    disabled={hasApplications}
                                    placeholder="Loan duration"
                                    {...formik.getFieldProps("loanDuration")}
                                />
                                {formik.touched.loanDuration && formik.errors.loanDuration && (
                                    <div className="text-danger">{formik.errors.loanDuration}</div>
                                )}
                            </div>
                            <div className="col-lg-6">
                                <label htmlFor="interestRate" className="form-label">
                                    Interest Rate (%) <span className="asterisk">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    id="interestRate"
                                    placeholder="Interest rate"
                                    disabled={hasApplications}
                                    value={formik.values.interestRate}
                                    onChange={(e) => {
                                        let value = parseFloat(e.target.value);
                                        if (isNaN(value)) {
                                            formik.setFieldValue("interestRate", e.target.value);
                                        } else {
                                            if (value < 0) value = 0;
                                            formik.setFieldValue("interestRate", value);
                                        }
                                    }}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.interestRate && formik.errors.interestRate && (
                                    <div className="text-danger">{formik.errors.interestRate}</div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label htmlFor="termsAndConditions" className="form-label">
                                Terms & Conditions <span className="asterisk">*</span>
                            </label>
                            <textarea
                                className="form-control"
                                id="termsAndConditions"
                                rows={4}
                                disabled={hasApplications}
                                placeholder="Enter terms and conditions"
                                {...formik.getFieldProps("termsAndConditions")}
                            />
                            {formik.touched.termsAndConditions &&
                                formik.errors.termsAndConditions && (
                                    <div className="text-danger">{formik.errors.termsAndConditions}</div>
                                )}
                        </div>

                        <div className="create-offr-btn text-center">
                            <button
                                type="submit"
                                className="btn btn-lender create-new-offer button-create"
                                disabled={btnLoading}
                            >
                                {btnLoading ? "Updating..." : "Update Offer"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UpdateOffer;
